import { useTranslate } from '@tolgee/react';
import { useEffect, useState } from 'react';
import { Moment } from 'moment/moment';
import classNames from 'classnames';
import moment from 'moment-timezone';
import useGetReservationAvailability from '@app/page/table-reservation/logic/use-get-reservation-availability.ts';
import { ReservationTimeSlot } from '@app/graphql/types/graphql.ts';
import Loading from '@app/components/loading.tsx';

export default function TableReservationDateInput(props: {
  currentDateValue: string;
  currentTimeValue: string;
  handleDate: (value: string | number) => void;
  handleTime: (value: string | number) => void;
  branchId: string;
  timezone: string;
}) {
  const { currentDateValue, currentTimeValue, handleDate, handleTime, branchId, timezone } = props;
  return (
    <div className="space-y-4">
      <DateInput
        currentDateValue={currentDateValue}
        handleDate={handleDate}
        handleTime={handleTime}
        timezone={timezone}
      />
      <TimeSlots
        currentTimeValue={currentTimeValue}
        branchId={branchId}
        handleTime={handleTime}
        timezone={timezone}
        currentDateValue={currentDateValue}
      />
    </div>
  );
}

function DateInput(props: {
  currentDateValue: string;
  handleDate: (value: string | number) => void;
  handleTime: (value: string | number) => void;
  timezone: string;
}) {
  const { t } = useTranslate();
  const { currentDateValue, handleDate, timezone, handleTime } = props;
  const momentDate = moment.tz(currentDateValue, timezone);

  useEffect(() => {
    if (!currentDateValue) {
      handleDate(moment.tz(timezone).format('YYYY-MM-DD'));
    }
  }, [currentDateValue, handleDate, timezone]);

  const handleDateIncrementAndDecrement = (action: string) => {
    switch (action) {
      case 'increment':
        handleDate(momentDate.clone().add(1, 'days').format('YYYY-MM-DD')); // Tạo bản sao trước khi thêm 1 ngày
        handleTime('');
        return;
      case 'decrement':
        if (!isPreviousValid(momentDate, timezone)) {
          handleDate(momentDate.clone().subtract(1, 'day').format('YYYY-MM-DD')); // Tạo bản sao trước khi trừ 1 ngày
          handleTime('');
        }
        return;
      case 'today':
        handleDate(moment.tz(props.timezone).format('YYYY-MM-DD')); // Không cần clone vì bạn đang tạo một Moment mới
        handleTime('');
        return;
      default:
        throw new Error('Wrong action!!');
    }
  };

  return (
    <div>
      <div>
        <label className="mb-1 block text-sm font-medium leading-6 text-gray-900">
          {t('reservation.create-form.form-input.date.label', 'Date')}
        </label>

        <input
          type="date"
          className="w-full rounded border border-gray-300 px-3 py-2"
          value={momentDate.format('YYYY-MM-DD')}
          onChange={(e) => {
            handleDate(moment(e.target.value, 'YYYY-MM-DD').format('YYYY-MM-DD'));
          }}
          min={moment.tz(timezone).format('YYYY-MM-DD')}
        />
      </div>

      <div className="grid grid-cols-3 gap-2 pt-2">
        <div
          onClick={() => {
            handleDateIncrementAndDecrement('decrement');
          }}
          className={classNames(
            'flex cursor-pointer items-center justify-center rounded px-2 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
            {
              'bg-gray-100 hover:bg-gray-100': isPreviousValid(momentDate, timezone),
            }
          )}
        >
          {t('reservation.create-form.form-input.date.button.previous', 'Previous')}
        </div>
        <div
          onClick={() => {
            handleDateIncrementAndDecrement('today');
          }}
          className={classNames(
            'flex cursor-pointer items-center justify-center rounded px-2 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-indigo-400',
            {
              'bg-indigo-600 text-white': momentDate.isSame(moment.tz(timezone), 'date'),
            }
          )}
        >
          {t('reservation.create-form.form-input.date.button.today', 'Today')}
        </div>

        <div
          onClick={() => {
            handleDateIncrementAndDecrement('increment');
          }}
          className="flex cursor-pointer items-center justify-center rounded px-2 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
        >
          {t('reservation.create-form.form-input.date.button.next', 'Next')}
        </div>
      </div>
    </div>
  );
}

function TimeSlots(props: {
  timezone: string;
  branchId: string;
  currentDateValue: string;
  currentTimeValue: string;
  handleTime: (value: string | number) => void;
}) {
  const { timezone, branchId, currentDateValue, currentTimeValue, handleTime } = props;
  const { t } = useTranslate();
  const { data, loading } = useGetReservationAvailability(
    branchId,
    moment.tz(currentDateValue, 'YYYY-MM-DD', timezone).set('hour', 6).format('YYYY-MM-DDTHH:mm')
  );
  const [timeSlots, setTimeSlots] = useState<ReservationTimeSlot[] | undefined>(undefined);

  useEffect(() => {
    setTimeSlots(data?.storefront_getTableReservationAvailability.slots.map((item) => item));
  }, [data, setTimeSlots]);

  if (loading) {
    return <Loading />;
  }

  if (!timeSlots || timeSlots.length == 0) {
    return (
      <div className="flex w-full items-center justify-center p-4 text-sm text-gray-400">
        {t('storefront-rerservation.create.service-and-time.form-input.date.no-time', 'No available time')}
      </div>
    );
  }

  const handleCLick = (value: string) => {
    const formatedHourString = moment.tz(value, timezone).format('HH:mm');
    handleTime(formatedHourString);
  };

  return (
    <div className="">
      <div className="grid max-h-80 grid-cols-3 gap-2 overflow-scroll rounded-xl bg-neutral-200 p-2 py-4 [&::-webkit-scrollbar]:w-0">
        {timeSlots.map((time) => {
          return (
            <div
              key={time.startTime}
              onClick={() => {
                if (time.spotsOpen > 0) {
                  handleCLick(time.startTime);
                }
              }}
              className={classNames(
                'flex cursor-pointer items-center justify-center gap-x-1 rounded-lg px-2 py-2 text-sm font-semibold text-gray-900 hover:bg-indigo-600 hover:text-white',
                {
                  'opacity-50 hover:bg-neutral-200 hover:text-gray-900': time.spotsOpen <= 0,
                },
                {
                  'bg-indigo-600 text-white': currentTimeValue == moment.tz(time.startTime, timezone).format('HH:mm'),
                }
              )}
            >
              <span>{moment.tz(time.startTime, timezone).format('HH:mm')}</span>
              {time.spotsOpen <= 0 && <span className="font-light italic">{t('reservation.full-slot', '(Full)')}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function isPreviousValid(date: Moment, timezone: string) {
  return moment.tz(timezone).isSame(date, 'date');
}
